/**
 * DecreeProcedureRunControls.tsx — „Uruchom procedurę dekretacji", renderowane OBOK
 * `DecreeSuggestControls` na zakładce Dekretacja obu klas faktur (Purchase/Sale). Te same
 * warunki widoczności co edytor (`editing && canManageAccounting`) — rodzic
 * (PurchaseInvoiceDetail/SaleInvoiceDetail) bramkuje to przed zamontowaniem.
 *
 * Analog „Podpowiedz dekret", ale źródłem propozycji jest AUTORSKA procedura SQL tenanta
 * (katalog `decree_procedures`), nie deklaratywny szablon: picker aktywnych procedur firmy+klasy
 * (`GET /decree-procedures?...&active=true`, zawiera też procedury company_id=NULL „dla wszystkich
 * firm") + przycisk wołający `POST /decree-procedures/run`. Wynik WYPEŁNIA draft
 * (`onApply`, na stronie faktury mapuje się na `handleDecreeChange` → oznacza dirty i ZASTĘPUJE
 * cały dotychczasowy szkic, nie dogrywa do niego). NIC nie jest zapisywane tutaj — zapis idzie
 * WYŁĄCZNIE przez zwykły zapis faktury (CLAUDE.md §4.14, ten komponent nie otwiera żadnej
 * transakcji).
 *
 * Kontrakt wyniku procedury (`/decree-procedures/run`) MA TERAZ te same segmenty konta co
 * edytor — `mainAccount`+`subAccount1..5`, BEZ walidacji/rozwiązania względem
 * `chart_of_accounts` (backend przepuszcza je wprost z wyniku SQL). To świadoma asymetria z
 * „Podpowiedz dekret" (`DecreeSuggestControls.tsx`), które zostaje na starym kontrakcie
 * (`accountNumber` + walidacja słownika) — osobna, nierozstrzygnięta jeszcze decyzja.
 *
 * Mapowanie błędów `POST .../run` (`mapRunErrorTitle`) idzie WYŁĄCZNIE przez wspólne mapowanie
 * `decreeProcedureSqlErrorKey` (`lib/decree-procedures/error-keys.ts` — `PROCEDURE_INACTIVE`,
 * `PROCEDURE_NOT_FOUND`, `SQL_*`, `DECREE_PROCEDURE_*`, `EXECUTION_FAILED`…); kod bez
 * dedykowanego klucza spada na w pełni generyczny toast. Brak dedykowanej obsługi
 * `DECREE_SUGGEST_ACCOUNT_INVALID` — backend go już nie zwraca (usunięta walidacja konta).
 *
 * Wymaga zapisanego dokumentu (`documentId`) — procedura czyta dane dokumentu po jego id.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Play } from 'lucide-react';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import {
  DECREE_PROCEDURE_SQL_ERROR_GENERIC_KEY,
  decreeProcedureSqlErrorKey,
} from '@/lib/decree-procedures/error-keys';
import { type DecreeLineDraft, normalizeBehindPosting } from '@/lib/documents/decree-lines';
import type {
  DocumentVatRegisterRowDraft,
  VatRegisterCategoryAbcdDraft,
} from '@/lib/documents/document-vat-register';

export type DecreeProcedureDocumentClass = 'purchase_invoice' | 'sale_invoice';

interface DecreeProcedureSummary {
  id: string;
  name: string;
}

interface DecreeProceduresListResponse {
  procedures: DecreeProcedureSummary[];
}

interface RunResponseLine {
  groupNo: number;
  side: 'debit' | 'credit';
  mainAccount: string;
  subAccount1: string | null;
  subAccount2: string | null;
  subAccount3: string | null;
  subAccount4: string | null;
  subAccount5: string | null;
  amount: string;
  description: string | null;
  /** Znaczniki wiersza — serwer zawsze je zwraca (domyślne 0/false, gdy procedura ich nie liczy). */
  behindPosting: number;
  isTransaction: boolean;
}

/** Wiersz rejestru VAT z odpowiedzi — kształt payloadu zapisu (`vatRegister`). */
interface RunResponseVatRow {
  registerCode: string;
  categoryAbcd?: string | null;
  vatRateCode: string;
  vatRate: string;
  valueNet: string;
  valueVat: string;
  valueGross: string;
  valueNetCurrency?: string | null;
  valueVatCurrency?: string | null;
  valueGrossCurrency?: string | null;
  period?: string | null;
  description?: string | null;
  ue?: boolean;
  service?: boolean;
  taxCategory?: string | null;
  gtuCodes?: string[] | null;
}

interface RunResponse {
  lines: RunResponseLine[];
  /** Puste, gdy procedura nie zwraca wierszy `row_kind='vat'`. */
  vatRegister?: RunResponseVatRow[];
}

export interface DecreeProcedureRunControlsProps {
  /** `null`/`undefined` gdy dokument jeszcze nie zapisany — akcja wyłączona. */
  documentId: string | null | undefined;
  companyId: string | null | undefined;
  documentClass: DecreeProcedureDocumentClass;
  /** Wynik procedury wypełnia draft edytora (`decreeDraft` na stronie faktury). */
  onApply: (lines: DecreeLineDraft[]) => void;
  /**
   * Wiersze `row_kind='vat'` z wyniku procedury → draft RĘCZNEGO rejestru VAT
   * (`vatRegisterDraft` na stronie faktury). Wołane TYLKO gdy procedura je zwróciła — pusty
   * zestaw NIE czyści siatki, żeby procedura pisząca wyłącznie dekret nie wywalała rejestru
   * wpisanego ręcznie.
   */
  onApplyVatRegister?: (rows: DocumentVatRegisterRowDraft[]) => void;
  'data-testid'?: string;
}

/**
 * Wiersz rejestru VAT z odpowiedzi procedury → draft siatki. Konwencja draftu: `''` znaczy
 * „nie podano" (mirror `serverVatRegisterRowToDraft`), więc null-e spłaszczamy do pustych
 * stringów; `key` nadajemy świeżo po stronie klienta (React key), bo wiersz jeszcze nie
 * istnieje w bazie.
 */
function runResponseVatRowToDraft(row: RunResponseVatRow): DocumentVatRegisterRowDraft {
  return {
    key: crypto.randomUUID(),
    registerCode: row.registerCode,
    categoryAbcd: (row.categoryAbcd ?? '') as VatRegisterCategoryAbcdDraft,
    vatRateCode: row.vatRateCode,
    vatRate: row.vatRate,
    valueNet: row.valueNet,
    valueVat: row.valueVat,
    valueGross: row.valueGross,
    valueNetCurrency: row.valueNetCurrency ?? '',
    valueVatCurrency: row.valueVatCurrency ?? '',
    valueGrossCurrency: row.valueGrossCurrency ?? '',
    service: row.service ?? false,
    taxCategory: row.taxCategory ?? '',
    period: row.period ?? '',
    description: row.description ?? '',
    ue: row.ue ?? false,
    gtuCodes: row.gtuCodes ?? [],
  };
}

function errorBody(err: unknown): Record<string, unknown> | null {
  if (isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
    return err.response.data as Record<string, unknown>;
  }
  return null;
}

export function DecreeProcedureRunControls({
  documentId,
  companyId,
  documentClass,
  onApply,
  onApplyVatRegister,
  'data-testid': testId = 'decree-procedure-run-controls',
}: DecreeProcedureRunControlsProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [selectedProcedureId, setSelectedProcedureId] = useState('');

  const proceduresQ = useQuery({
    queryKey: ['decree-procedures', 'picker', companyId, documentClass],
    queryFn: async () => {
      const params = new URLSearchParams({ documentClass, active: 'true' });
      if (companyId) params.set('companyId', companyId);
      const res = await api.get<DecreeProceduresListResponse>(
        `/decree-procedures?${params.toString()}`,
      );
      return res.data;
    },
    enabled: !!companyId,
    staleTime: 30_000,
  });
  const procedures = proceduresQ.data?.procedures ?? [];

  function mapRunErrorTitle(err: unknown): string {
    const body = errorBody(err);
    const code = typeof body?.error === 'string' ? body.error : undefined;
    // Mapowanie kodów SQL/procedury (`PROCEDURE_INACTIVE`,
    // `PROCEDURE_NOT_FOUND`, `SQL_*`, `DECREE_PROCEDURE_*`, `EXECUTION_FAILED`…) — bez tego user
    // dostawał wyłącznie w pełni generyczny toast, tracąc realny powód odrzucenia przez backend.
    const sqlErrorKey = decreeProcedureSqlErrorKey(code);
    if (sqlErrorKey !== DECREE_PROCEDURE_SQL_ERROR_GENERIC_KEY) {
      const translated = t(sqlErrorKey, {
        paramName: typeof body?.paramName === 'string' ? body.paramName : '',
      });
      // EXECUTION_FAILED niesie wyłącznie `sqlState` (kontrakt bez surowego komunikatu Postgresa,
      // przeciek danych) — dokładamy kod pod generycznym tekstem, diagnostyka bez ekspozycji treści.
      if (code === 'EXECUTION_FAILED' && typeof body?.sqlState === 'string') {
        return `${translated} (${body.sqlState})`;
      }
      return translated;
    }
    return t('tenant.documents.decree.procedures.toast.genericError');
  }

  const runMutation = useMutation({
    mutationFn: async () => {
      if (!documentId || !selectedProcedureId) throw new Error('NOT_READY');
      const res = await api.post<RunResponse>('/decree-procedures/run', {
        documentId,
        procedureId: selectedProcedureId,
      });
      return res.data;
    },
    onSuccess: (data) => {
      onApply(
        data.lines.map((line) => ({
          key: crypto.randomUUID(),
          groupNo: line.groupNo,
          side: line.side,
          mainAccount: line.mainAccount,
          subAccount1: line.subAccount1 ?? undefined,
          subAccount2: line.subAccount2 ?? undefined,
          subAccount3: line.subAccount3 ?? undefined,
          subAccount4: line.subAccount4 ?? undefined,
          subAccount5: line.subAccount5 ?? undefined,
          amount: line.amount,
          description: line.description ?? undefined,
          behindPosting: normalizeBehindPosting(line.behindPosting),
          isTransaction: line.isTransaction,
        })),
      );
      const vatRows = data.vatRegister ?? [];
      if (onApplyVatRegister && vatRows.length > 0) {
        onApplyVatRegister(vatRows.map(runResponseVatRowToDraft));
      }
      toast.show({
        variant: 'success',
        title: t('tenant.documents.decree.procedures.toast.applied'),
      });
    },
    onError: (err) => {
      toast.show({ variant: 'error', title: mapRunErrorTitle(err) });
    },
  });

  const canRun = !!documentId && !!selectedProcedureId && !runMutation.isPending;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testId}>
      <Dropdown
        options={[
          { value: '', label: t('tenant.documents.decree.procedures.pickerPlaceholder') },
          ...procedures.map((proc) => ({ value: proc.id, label: proc.name })),
        ]}
        value={selectedProcedureId}
        onChange={(v) => setSelectedProcedureId(v)}
        disabled={!documentId || proceduresQ.isLoading}
        aria-label={t('tenant.documents.decree.procedures.pickerLabel')}
        className="w-56"
        data-testid={`${testId}-procedure-picker`}
      />
      <Button
        variant="secondary"
        size="sm"
        onClick={() => runMutation.mutate()}
        disabled={!canRun}
        loading={runMutation.isPending}
        data-testid={`${testId}-run-btn`}
      >
        <Play className="mr-1 h-3.5 w-3.5" />
        {t('tenant.documents.decree.procedures.runButton')}
      </Button>
    </div>
  );
}
